import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useAuthStore } from './store/authStore';
import { authApi } from './services/portalApi';

import ProtectedRoute from './components/ProtectedRoute';
import Layout from './components/Layout';

import LoginPage from './pages/LoginPage';
import Landing from './pages/public/Landing';
import Register from './pages/public/Register';
import RegisterSuccess from './pages/public/RegisterSuccess';
import RegisterCancelled from './pages/public/RegisterCancelled';
import ForgotPassword from './pages/public/ForgotPassword';
import ResetPassword from './pages/public/ResetPassword';
import AppSetup from './pages/public/AppSetup';
import Privacy from './pages/public/Privacy';
import Terms from './pages/public/Terms';

import AdminDashboard from './pages/admin/Dashboard';
import AdminGuards from './pages/admin/Guards';
import AdminPortalUsers from './pages/admin/PortalUsers';
import AdminPatrols from './pages/admin/Patrols';
import AdminEvents from './pages/admin/Events';
import AdminSettings from './pages/admin/Settings';
import AdminReports from './pages/admin/Reports';
import AdminDrones from './pages/admin/Drones';
import SystemHealth from './pages/SystemHealth';

import GuardhouseDashboard from './pages/guardhouse/Dashboard';
import GuardhouseGuards from './pages/guardhouse/Guards';
import GuardhousePatrols from './pages/guardhouse/Patrols';
import GuardhouseEvents from './pages/guardhouse/Events';
import GuardhouseReports from './pages/guardhouse/Reports';

import TrusteeDashboard from './pages/trustee/Dashboard';
import TrusteeGuards from './pages/trustee/Guards';
import TrusteePortalUsers from './pages/trustee/PortalUsers';
import TrusteePatrols from './pages/trustee/Patrols';
import TrusteeEvents from './pages/trustee/Events';
import TrusteeSettings from './pages/trustee/Settings';

/**
 * On mount: if the access token is expired but a refresh token exists,
 * silently renew before the rest of the app renders. This means users
 * with a valid refresh token (up to 30 days) never see the login page
 * just because they left overnight.
 */
function AuthInit({ children }: { children: React.ReactNode }) {
  const { accessToken, refreshToken, payload, setTokens, clearTokens } = useAuthStore();
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const isExpired = payload ? payload.exp * 1000 <= Date.now() : true;

    if (accessToken && !isExpired) {
      // Token still valid — nothing to do
      setReady(true);
      return;
    }

    if (refreshToken) {
      // Access token missing or expired — try a silent refresh
      authApi.refresh(refreshToken)
        .then((tokens) => setTokens(tokens.access_token, tokens.refresh_token))
        .catch(() => clearTokens())
        .finally(() => setReady(true));
    } else {
      setReady(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!ready) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="w-6 h-6 border-2 border-brand-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return <>{children}</>;
}

function RoleRedirect() {
  const role = useAuthStore((s) => s.role());
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated());
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <Navigate to={role ? `/${role}/dashboard` : '/admin/dashboard'} replace />;
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthInit>
      <Routes>
        {/* Public */}
        <Route path="/" element={<Landing />} />
        <Route path="/register" element={<Register />} />
        <Route path="/register/success" element={<RegisterSuccess />} />
        <Route path="/register/cancelled" element={<RegisterCancelled />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        {/* Patroller-facing app setup, reached from the link in admin Settings.
            Public: patrollers have no portal login. */}
        <Route path="/setup" element={<AppSetup />} />
        {/* Public URL required by the Play Store and App Store — a modal inside
            the portal login does not qualify and a reviewer cannot reach one. */}
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/login" element={<LoginPage />} />

        {/* Auto-redirect authenticated root */}
        <Route path="/app" element={<RoleRedirect />} />

        {/* Admin */}
        <Route element={<ProtectedRoute allowedRoles={['admin']} />}>
          <Route element={<Layout />}>
            <Route path="/admin/dashboard"    element={<AdminDashboard />} />
            <Route path="/admin/guards"       element={<AdminGuards />} />
            <Route path="/admin/portal-users" element={<AdminPortalUsers />} />
            <Route path="/admin/patrols"      element={<AdminPatrols />} />
            <Route path="/admin/drones"       element={<AdminDrones />} />
            <Route path="/admin/events"       element={<AdminEvents />} />
            <Route path="/admin/settings"     element={<AdminSettings />} />
            <Route path="/admin/reports"      element={<AdminReports />} />
            <Route path="/admin/health"       element={<SystemHealth />} />
            {/* Legacy redirects */}
            <Route path="/admin/incidents" element={<Navigate to="/admin/events" replace />} />
            <Route path="/admin/alarms"    element={<Navigate to="/admin/events" replace />} />
          </Route>
        </Route>

        {/* Guardhouse */}
        <Route element={<ProtectedRoute allowedRoles={['guardhouse']} />}>
          <Route element={<Layout />}>
            <Route path="/guardhouse/dashboard"  element={<GuardhouseDashboard />} />
            <Route path="/guardhouse/guards"     element={<GuardhouseGuards />} />
            <Route path="/guardhouse/patrols"    element={<GuardhousePatrols />} />
            <Route path="/guardhouse/events"     element={<GuardhouseEvents />} />
            <Route path="/guardhouse/reports"    element={<GuardhouseReports />} />
            <Route path="/guardhouse/health"     element={<SystemHealth />} />
            {/* Legacy redirects */}
            <Route path="/guardhouse/incidents" element={<Navigate to="/guardhouse/events" replace />} />
            <Route path="/guardhouse/alarms"    element={<Navigate to="/guardhouse/events" replace />} />
          </Route>
        </Route>

        {/* Trustee */}
        <Route element={<ProtectedRoute allowedRoles={['trustee']} />}>
          <Route element={<Layout />}>
            <Route path="/trustee/dashboard"    element={<TrusteeDashboard />} />
            <Route path="/trustee/guards"       element={<TrusteeGuards />} />
            <Route path="/trustee/portal-users" element={<TrusteePortalUsers />} />
            <Route path="/trustee/patrols"      element={<TrusteePatrols />} />
            <Route path="/trustee/events"       element={<TrusteeEvents />} />
            <Route path="/trustee/settings"     element={<TrusteeSettings />} />
            <Route path="/trustee/health"       element={<SystemHealth />} />
            {/* Legacy redirects */}
            <Route path="/trustee/incidents" element={<Navigate to="/trustee/events" replace />} />
            <Route path="/trustee/alarms"    element={<Navigate to="/trustee/events" replace />} />
          </Route>
        </Route>

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </AuthInit>
    </BrowserRouter>
  );
}
